import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const CategoriesSection = ({ categories, navigate }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // iOS Safari fallback: show after 2s if observer doesn't trigger
    const fallback = setTimeout(() => setIsVisible(true), 2000);

    return () => {
      clearTimeout(fallback);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} data-testid="categories-section" className="py-12 sm:py-20 bg-gray-50 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]"></div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-8 sm:mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 bg-purple-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
            <span className="text-purple-600 font-semibold text-xs sm:text-sm">NUESTROS SERVICIOS</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-6">
            Especialidades y Categorías
          </h2>
          <p className="text-sm sm:text-xl text-gray-600">
            Más de 60 trámites especializados en 17 categorías principales
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
          {Array.isArray(categories) && categories.map((category, index) => (
            <CategoryCard
              key={category._id || category.slug}
              category={category}
              navigate={navigate}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const CategoryCard = ({ category, navigate, index, isVisible }) => {
  const delay = `${index * 50}ms`;

  return (
    <div
      data-testid={`category-card-${category.slug}`}
      onClick={() => navigate(`/categoria/${category.slug}`)}
      className={`group relative bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border border-gray-100 transform hover:-translate-y-2 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      }`}
      style={{ transitionDelay: delay }}
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-pink-500/5 transition-all duration-500"></div>

      <div className="relative z-10">
        {/* Icon */}
        <div className="text-3xl sm:text-5xl mb-2 sm:mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 inline-block">
          {category.icon}
        </div>

        {/* Badge de número de trámites */}
        <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
          {category.application_type_count} trámites
        </div>

        <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-blue-600 transition-colors duration-300 leading-tight">
          {category.name}
        </h3>
        <p className="text-gray-600 text-xs sm:text-sm mb-2 sm:mb-4 line-clamp-2">{category.description}</p>

        {/* Arrow indicator */}
        <div className="hidden sm:flex items-center text-blue-600 font-medium text-sm opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300">
          <span>Ver más</span>
          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>

      {/* Decorative corner */}
      <div className="absolute bottom-0 right-0 w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-tl from-blue-500/10 to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    </div>
  );
};

export default CategoriesSection;
